import React, { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { UserOutlined, SettingOutlined, LogoutOutlined, LockOutlined, DownOutlined } from '@ant-design/icons'
import { Layout, Menu, Avatar, Dropdown, Modal, Form, Input, MenuProps, message, Typography, Tag, Progress } from 'antd'
import { getMenus } from '@/routes/menus'
import NProgress from '@/components/NProgress'
import './style.less'
import { clearUser, getDisplayName, getRole, subscribeAuth, getAuthSnapshot } from '@/utils/auth'
import { getPasswordStrength, passwordPolicyValidator } from '@/utils/password'
import { roleTextMap } from '@/utils/role'
import { changePassword } from '@/api'
import { logout, type ChangePasswordPayload } from '@/api/login'
import smartpntLogo from '@/assets/brand/smartpnt-logo.png'

const { Header, Content, Sider } = Layout

type LayoutMenuItem = {
  key: string
  label: string
  children?: LayoutMenuItem[]
}

export interface LayoutProps {}

const AppLayout: React.FC<LayoutProps> = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [headerSelectKey, setHeaderSelectKey] = useState<string>('')
  const [messageApi, contextHolder] = message.useMessage()
  const location = useLocation()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newPwd, setNewPwd] = useState('')
  const [sliderMenuOpenKeys, setSliderMenuOpenKeys] = useState<string[]>([])

  const authSnapshot = useSyncExternalStore(subscribeAuth, getAuthSnapshot)
  const currentMenus = useMemo(() => getMenus() as LayoutMenuItem[], [authSnapshot])
  const siderMenus = useMemo(() => currentMenus, [currentMenus, headerSelectKey])
  const passwordStrength = getPasswordStrength(newPwd)

  useEffect(() => {
    const { paths } = currentMenus?.reduce((r, menu) => {
      if (Array.isArray(menu.children)) {
        const findItem = menu.children.find((item) => item.key === location.pathname)
        if (findItem) {
          r.paths.push(menu.key)
          return r
        }
      }
      return r
    }, { paths: [] } as { paths: string[] })
    const { titles } = currentMenus?.reduce((r, menu) => {
      if (location.pathname === '/auth/login') {
        r.titles.push('登录')
      } else if (menu.key === location.pathname) {
        r.titles.push(menu.label)
      } else if (Array.isArray(menu.children)) {
        const findItem = menu.children.find((item) => item.key === location.pathname)
        if (findItem) {
          r.titles.push(findItem.label || menu.label)
          return r
        }
      }
      return r
    }, { titles: [] } as { titles: string[] })
    if (titles[titles.length - 1]) {
      document.title = titles[titles.length - 1]
      localStorage.setItem('title', titles[titles.length - 1])
    } else {
      document.title = localStorage.getItem('title') || ''
    }
    setSliderMenuOpenKeys(paths)
  }, [location.pathname, currentMenus])

  useEffect(() => {
    const moduleRoute = location.pathname.match(/(?<!\w)\/\w+/g)
    if (Array.isArray(moduleRoute)) {
      setHeaderSelectKey(moduleRoute[0])
    }
  }, [])

  if (location.pathname === '/auth/login') {
    return <Outlet />
  }

  const handleOk = async () => {
    const values = await form.validateFields()
    const payload: ChangePasswordPayload = {
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    }
    changePassword(payload)
      .then(resp => {
        if (resp.success) {
          messageApi.open({ type: 'success', content: '修改成功，请重新登录' })
          setIsModalOpen(false)
          form.resetFields()
          setNewPwd('')
          setTimeout(() => {
            clearUser()
            navigate('/auth/login')
          }, 800)
        } else {
          messageApi.open({ type: 'error', content: resp.errorMsg })
        }
      })
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    form.resetFields()
    setNewPwd('')
  }

  const displayName = getDisplayName()
  const role = getRole()

  const items: MenuProps['items'] = [
    {
      key: 'userInfo',
      label: (
        <div className="header-user-info">
          <Typography.Text strong>{displayName || 'admin'}</Typography.Text>
          <br />
          <Tag color="blue" style={{ marginTop: 4 }}>
            {roleTextMap[role] || role || '用户'}
          </Tag>
        </div>
      ),
      disabled: true,
    },
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: '个人中心',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'password',
      icon: <LockOutlined />,
      label: '修改密码',
      onClick: () => {
        form.resetFields()
        setNewPwd('')
        setIsModalOpen(true)
      },
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: async () => {
        try {
          const response = await logout()
          if (!response.success) throw new Error(response.errorMsg || '退出登录失败')
          clearUser()
          navigate('/auth/login')
        } catch {
          messageApi.error('退出登录失败，请重试')
        }
      },
    },
  ]

  return (
    <Layout className="main-layout">
      <NProgress />
      {contextHolder}
      <Header className="main-header">
        <div className="header-left" onClick={() => navigate('/home/dashboard')}>
          <span className="header-brand-logo" aria-label="SmartPNT">
            <img src={smartpntLogo} alt="SmartPNT" />
          </span>
        </div>
        <div className="header-right">
          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            arrow={{ pointAtCenter: true }}
            trigger={['click']}
          >
            <div className="header-user" title="个人中心">
              <Avatar icon={<UserOutlined />} size={28} className="header-avatar" />
              <span className="header-username">{displayName || 'admin'}</span>
              <span className="header-user-trigger">
                <DownOutlined />
              </span>
            </div>
          </Dropdown>
        </div>
      </Header>
      <Layout className="main-body" hasSider>
        <Sider
          width={220}
          collapsedWidth={64}
          breakpoint="md"
          className="main-sider"
          collapsible
          collapsed={collapsed}
          onBreakpoint={broken => setCollapsed(broken)}
          onCollapse={value => setCollapsed(value)}
        >
          <Menu
            mode="inline"
            items={siderMenus}
            openKeys={sliderMenuOpenKeys}
            onOpenChange={openKeys => setSliderMenuOpenKeys(openKeys)}
            selectedKeys={[location.pathname]}
            onClick={({ key }) => navigate(key)}
          />
        </Sider>
        <Layout className="main-container" style={{ marginLeft: collapsed ? 64 : 220 }}>
          <Content className="main-view">
            <Outlet />
          </Content>
        </Layout>
      </Layout>

      <Modal
        title="修改密码"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="确定"
        cancelText="取消"
        maskClosable={false}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="mt20">
          <Form.Item
            label="原密码"
            name="currentPassword"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password placeholder="请输入原密码" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { validator: passwordPolicyValidator },
            ]}
          >
            <Input.Password
              placeholder="至少8位，含大小写字母和数字"
              onChange={(e) => setNewPwd(e.target.value)}
            />
          </Form.Item>
          {newPwd && (
            <Form.Item label="密码强度">
              <Progress
                percent={passwordStrength.percent}
                status={passwordStrength.status}
                format={() => passwordStrength.text}
                size="small"
              />
            </Form.Item>
          )}
          <Form.Item
            label="确认密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator (_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('两次密码输入不一致'))
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}

export default AppLayout
